import { css } from '@emotion/react'
import {
  Button,
  ButtonGroup,
  Flow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  Tag,
  Text,
  Theme,
  useTheme,
} from 'bold-ui'
import { Form, FormPrompt, FormRenderProps } from 'components/form'
import { Fragment, useState } from 'react'
import { metaPath } from 'util/metaPath'

import {
  headerContentTab,
  SolicitacaoRegulacaoModel,
  tabLabelByTipo,
  TipoSolicitacaoRegulacaoEnum,
} from '../model-esusRegulacao'
import { EsusRegulacaoFormFields } from './EsusRegulacaoFormFields'
import { solicitacaoRegulacaoValidator } from './validator-esusRegulacao'

interface EsusRegulacaoModalFormProps {
  tipoInicial: TipoSolicitacaoRegulacaoEnum
  tiposDisponiveis?: TipoSolicitacaoRegulacaoEnum[]
  onClose: () => void
  onSubmit: (values: SolicitacaoRegulacaoModel) => void
}

const path = metaPath<SolicitacaoRegulacaoModel>()

export const EsusRegulacaoModalForm = (props: Readonly<EsusRegulacaoModalFormProps>) => {
  const { tipoInicial, tiposDisponiveis = [tipoInicial], onClose, onSubmit } = props

  // Esse const id é pra suportar edição futura de uma solicitação já existente
  // (mesmo padrão do EditarCuidadoCompartilhadoModalForm).
  // const { id } = useParams<{ id?: string }>()

  const theme = useTheme()
  const styles = createStyles(theme)

  const [tipo, setTipo] = useState<TipoSolicitacaoRegulacaoEnum>(tipoInicial)

  const { title, description } = headerContentTab[tipo]
  const showTipoSwitcher = tiposDisponiveis.length > 1

  const handleSubmit = (values: SolicitacaoRegulacaoModel) => {
    onSubmit({ ...values, tipo })
    onClose()
  }

  const renderForm = (renderProps: FormRenderProps<SolicitacaoRegulacaoModel>) => (
    <Fragment>
      <FormPrompt />
      <Modal open onClose={onClose} size='large'>
        <ModalHeader style={styles.header}>
          <Flow direction='vertical' gap={0.5}>
            <Flow direction='horizontal' alignItems='center' gap={0.5}>
              <Text fontSize={1.125} fontWeight='bold'>
                {title}
              </Text>
              <Tag type='info'>e-SUS Regulação</Tag>
            </Flow>
            <Text>{description}</Text>
          </Flow>
        </ModalHeader>

        <ModalBody>
          <Flow direction='vertical'>
            {showTipoSwitcher && (
              <ButtonGroup>
                {tiposDisponiveis.map((tipoOption) => (
                  <Button
                    key={tipoOption}
                    size='small'
                    kind={tipo === tipoOption ? 'primary' : 'normal'}
                    onClick={() => setTipo(tipoOption)}
                  >
                    {tabLabelByTipo[tipoOption]}
                  </Button>
                ))}
              </ButtonGroup>
            )}

            <EsusRegulacaoFormFields path={path} tipo={tipo} />
          </Flow>
        </ModalBody>

        <ModalFooter>
          <Flow direction='horizontal' justifyContent='flex-end'>
            <ModalFooterButton kind='normal' onClick={onClose}>
              Cancelar
            </ModalFooterButton>
            <ModalFooterButton kind='primary' onClick={renderProps.handleSubmit}>
              Salvar
            </ModalFooterButton>
          </Flow>
        </ModalFooter>
      </Modal>
    </Fragment>
  )

  return (
    <Form<SolicitacaoRegulacaoModel>
      // TODO (@Sirius): quando existir edição, buscar o item por `id` e usar como initialValues
      initialValues={{ tipo: tipoInicial }}
      validate={solicitacaoRegulacaoValidator}
      onSubmit={handleSubmit}
      render={renderForm}
    />
  )
}

const createStyles = (theme: Theme) => ({
  header: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
})
